import { DEFAULT_REFRESH_THRESHOLD_MS } from "./config";
import { isFresh } from "./credStore";
import type { Creds, FetchCreds } from "./types";

export interface CredManagerDeps {
  /** Obtain a fresh cred bundle from the host. */
  fetchCreds: FetchCreds;
  /** Refresh tokens this close (ms) to expiry. Defaults to {@link DEFAULT_REFRESH_THRESHOLD_MS}. */
  refreshThresholdMs?: number;
  /** Clock source, injectable for tests. Defaults to `Date.now`. */
  now?: () => number;
}

export interface CredManager {
  /** Return a usable token for the active org, refreshing first if stale. Single-flight. */
  getValidToken(): Promise<string>;
  /** Set the current org (e.g. on a `type:'org'` context.update); prefetches if not cached fresh. */
  setActiveOrg(orgId: string): void;
  /** Drop a cached cred (default: active org) so the next `getValidToken` re-pulls. Use on 401. */
  invalidate(orgId?: string): void;
  /** The org currently in scope, or `undefined` before the first creds arrive. */
  getActiveOrg(): string | undefined;
  /** User and org of the cached creds for the active org (no token), or `undefined` if none. */
  getActiveIdentity(): { userId: string; orgId: string } | undefined;
}

/** Key for the single-flight map when no active org is known yet (bootstrap fetch). */
const BOOTSTRAP_KEY = Symbol("bootstrap");

/**
 * In-memory, org-keyed credential cache with just-in-time, single-flight refresh. Holds no
 * timers and no host knowledge — it is driven entirely by `getValidToken`/`setActiveOrg` calls
 * and the injected `fetchCreds`. Tokens live only in memory; a reload re-pulls from the host.
 */
export function createCredManager(deps: CredManagerDeps): CredManager {
  const fetchCreds = deps.fetchCreds;
  const thresholdMs = deps.refreshThresholdMs ?? DEFAULT_REFRESH_THRESHOLD_MS;
  const now = deps.now ?? Date.now;

  const store = new Map<string, Creds>();
  const inFlight = new Map<string | symbol, Promise<Creds>>();
  let activeOrgId: string | undefined;

  function cachedFresh(orgId: string): Creds | undefined {
    const cached = store.get(orgId);
    return cached && isFresh(cached, now(), thresholdMs) ? cached : undefined;
  }

  function refresh(key: string | symbol): Promise<Creds> {
    const existing = inFlight.get(key);
    if (existing) return existing;

    const p = fetchCreds()
      .then((creds) => {
        // Always cache under the org the host actually returned (handles org races).
        store.set(creds.orgId, creds);
        if (activeOrgId === undefined) activeOrgId = creds.orgId;
        return creds;
      })
      .finally(() => {
        inFlight.delete(key);
      });

    inFlight.set(key, p);
    return p;
  }

  return {
    async getValidToken() {
      if (activeOrgId !== undefined) {
        const fresh = cachedFresh(activeOrgId);
        if (fresh) return fresh.token;
      }
      const creds = await refresh(activeOrgId ?? BOOTSTRAP_KEY);
      return creds.token;
    },

    setActiveOrg(orgId) {
      activeOrgId = orgId;
      if (!cachedFresh(orgId)) {
        // Prefetch so the first API call in the new org pays no latency. Errors surface on the
        // next getValidToken, so a failed prefetch is intentionally swallowed here.
        void refresh(orgId).catch(() => {});
      }
    },

    invalidate(orgId) {
      const target = orgId ?? activeOrgId;
      if (target !== undefined) store.delete(target);
    },

    getActiveOrg() {
      return activeOrgId;
    },

    getActiveIdentity() {
      const cached = activeOrgId === undefined ? undefined : store.get(activeOrgId);
      return cached ? { userId: cached.userId, orgId: cached.orgId } : undefined;
    },
  };
}
